import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { subscriptionAPI, paymentAPI } from '../lib/api';
import AlertDialog from '../components/AlertDialog';
import ConfirmDialog from '../components/ConfirmDialog';

const PurchaseAddon = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { tenantId } = useParams();
    const preselectedAddon = location.state?.addon;

    const [addons, setAddons] = useState([]);
    const [myAddons, setMyAddons] = useState([]);
    const [selectedAddon, setSelectedAddon] = useState(preselectedAddon || null);
    const [quantity, setQuantity] = useState(1);
    const [loading, setLoading] = useState(true);
    const [purchasing, setPurchasing] = useState(false);
    const [alert, setAlert] = useState({ isOpen: false, title: '', message: '', type: 'info' });
    const [confirm, setConfirm] = useState({ isOpen: false, title: '', message: '', onConfirm: null });
    const [orderResponse, setOrderResponse] = useState(null);

    const user = JSON.parse(localStorage.getItem('user') || '{}');

    // Get applicable plan type based on user role
    const getApplicablePlanType = () => {
        switch (user.role) {
            case 'STARTUP':
            case 'ENTREPRENEUR':
                return 'STARTUP';
            case 'INVESTOR':
                return 'INVESTOR';
            case 'INVESTOR_NETWORK':
                return 'INVESTOR_NETWORK';
            default:
                return null;
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const [addonsData, myAddonsData] = await Promise.all([
                subscriptionAPI.getAddons(),
                subscriptionAPI.getMyAddons().catch(() => [])
            ]);

            // Filter addons based on user role - only show applicable addons
            const applicablePlanType = getApplicablePlanType();
            const filteredAddons = addonsData.filter(addon => {
                if (!addon.applicablePlanTypes) return true;
                return addon.applicablePlanTypes.includes(applicablePlanType);
            });

            setAddons(filteredAddons);
            setMyAddons(myAddonsData);
            if (preselectedAddon) {
                setSelectedAddon(preselectedAddon);
            }
        } catch (error) {
            console.error('Failed to load data:', error);
            setAlert({ isOpen: true, title: 'Error', message: error.message, type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handlePurchase = () => {
        if (!selectedAddon) return;

        const totalAmount = selectedAddon.unitPrice * quantity;
        setConfirm({
            isOpen: true,
            title: 'Purchase Add-on',
            message: `Are you sure you want to purchase ${quantity}x ${selectedAddon.addonName} for ₹${totalAmount}?`,
            type: 'info',
            onConfirm: () => performPurchase()
        });
    };

    const performPurchase = async () => {
        setPurchasing(true);
        try {
            // For now, just purchase the addon directly
            // In production, you'd create an invoice and process payment
            await subscriptionAPI.purchaseAddon(selectedAddon.addonCode, quantity);
            setAlert({
                isOpen: true,
                title: 'Success',
                message: 'Add-on purchased successfully!',
                type: 'success'
            });
            loadData();
        } catch (error) {
            console.error('Purchase failed:', error);
            setAlert({ isOpen: true, title: 'Error', message: error.message || 'Purchase failed', type: 'error' });
        } finally {
            setPurchasing(false);
        }
    };

    const calculateTotal = () => {
        if (!selectedAddon) return 0;
        return selectedAddon.unitPrice * quantity;
    };

    const getTotalPrice = () => {
        return '₹' + calculateTotal().toLocaleString('en-IN');
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="p-6">
            {/* Header */}
            <div className="mb-8">
                <button
                    onClick={() => navigate(`/${tenantId}/my-subscription`)}
                    className="text-blue-600 hover:text-blue-800 mb-4 flex items-center"
                >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                    </svg>
                    Back to My Subscription
                </button>
                <h1 className="text-3xl font-bold text-gray-900">Purchase Add-ons</h1>
                <p className="text-gray-600 mt-1">Enhance your subscription with additional features</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Available Addons */}
                <div className="lg:col-span-2">
                    <div className="bg-white rounded-lg shadow p-6">
                        <h2 className="text-xl font-bold text-gray-900 mb-6">Available Add-ons</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {addons.map((addon) => {
                                const isSelected = selectedAddon?.id === addon.id;
                                const isPurchased = myAddons.some(ma => ma.addon?.id === addon.id && ma.status === 'ACTIVE');

                                return (
                                    <div
                                        key={addon.id}
                                        onClick={() => !isPurchased && setSelectedAddon(addon)}
                                        className={`relative p-6 rounded-lg border-2 cursor-pointer transition-all ${
                                            isSelected
                                                ? 'border-blue-500 bg-blue-50'
                                                : isPurchased
                                                    ? 'border-green-500 bg-green-50 cursor-not-allowed opacity-60'
                                                    : 'border-gray-200 hover:border-blue-300'
                                        }`}
                                    >
                                        {isPurchased && (
                                            <div className="absolute top-4 right-4 bg-green-500 text-white px-2 py-1 text-xs rounded">
                                                Purchased
                                            </div>
                                        )}
                                        {isSelected && (
                                            <div className="absolute top-4 right-4 bg-blue-500 text-white px-2 py-1 text-xs rounded">
                                                Selected
                                            </div>
                                        )}
                                        <h3 className="text-lg font-semibold text-gray-900 mb-2">{addon.addonName}</h3>
                                        <p className="text-gray-600 text-sm mb-4">
                                            {addon.addonCategory === 'STORAGE'
                                                ? `Add ${addon.unitValue} GB of additional storage`
                                                : `Add ${addon.unitValue} additional investor links`}
                                        </p>
                                        <div className="text-2xl font-bold text-blue-600">
                                            ₹{addon.unitPrice.toLocaleString('en-IN')}
                                        </div>
                                        <p className="text-xs text-gray-500 mt-1">per unit</p>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Purchased Addons */}
                    {myAddons.length > 0 && (
                        <div className="bg-white rounded-lg shadow p-6 mt-6">
                            <h2 className="text-xl font-bold text-gray-900 mb-6">Your Purchased Add-ons</h2>
                            <div className="space-y-4">
                                {myAddons.map((userAddon) => (
                                    <div key={userAddon.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                                        <div>
                                            <h3 className="font-semibold">{userAddon.addon?.addonName}</h3>
                                            <p className="text-sm text-gray-500">
                                                Qty: {userAddon.quantity} | Purchased: {new Date(userAddon.purchaseDate).toLocaleDateString()}
                                            </p>
                                        </div>
                                        <div className="text-right">
                                            <div className="font-semibold">{userAddon.totalAmount}</div>
                                            <span className={`px-2 py-1 rounded text-xs ${
                                                userAddon.status === 'ACTIVE' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                                            }`}>
                                                {userAddon.status}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Order Summary */}
                <div className="lg:col-span-1">
                    <div className="bg-white rounded-lg shadow p-6 sticky top-6">
                        <h2 className="text-xl font-bold text-gray-900 mb-6">Order Summary</h2>

                        {selectedAddon ? (
                            <>
                                <div className="mb-6">
                                    <h3 className="font-semibold text-gray-900 mb-2">{selectedAddon.addonName}</h3>
                                    <p className="text-sm text-gray-600 mb-4">
                                        {selectedAddon.addonCategory === 'STORAGE'
                                            ? `+${selectedAddon.unitValue} GB per unit`
                                            : `+${selectedAddon.unitValue} investor links per unit`}
                                    </p>

                                    <div className="flex items-center justify-between mb-4">
                                        <label className="text-sm font-medium text-gray-700">Quantity</label>
                                        <div className="flex items-center space-x-3">
                                            <button
                                                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                                className="w-8 h-8 rounded bg-gray-200 hover:bg-gray-300 flex items-center justify-center"
                                            >
                                                -
                                            </button>
                                            <span className="w-12 text-center font-semibold">{quantity}</span>
                                            <button
                                                onClick={() => setQuantity(quantity + 1)}
                                                className="w-8 h-8 rounded bg-gray-200 hover:bg-gray-300 flex items-center justify-center"
                                            >
                                                +
                                            </button>
                                        </div>
                                    </div>

                                    <div className="border-t pt-4 space-y-2">
                                        <div className="flex justify-between text-sm">
                                            <span className="text-gray-600">Unit Price</span>
                                            <span>₹{selectedAddon.unitPrice.toLocaleString('en-IN')}</span>
                                        </div>
                                        <div className="flex justify-between text-sm">
                                            <span className="text-gray-600">Quantity</span>
                                            <span>{quantity}</span>
                                        </div>
                                        <div className="flex justify-between text-lg font-bold pt-2 border-t">
                                            <span>Total</span>
                                            <span className="text-blue-600">{getTotalPrice()}</span>
                                        </div>
                                    </div>
                                </div>

                                <button
                                    onClick={handlePurchase}
                                    disabled={purchasing}
                                    className="w-full py-3 px-6 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {purchasing ? 'Processing...' : 'Purchase Now'}
                                </button>
                            </>
                        ) : (
                            <div className="text-center py-8 text-gray-500">
                                <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                                </svg>
                                <p>Select an add-on to purchase</p>
                            </div>
                        )}

                        <div className="mt-6 pt-6 border-t text-sm text-gray-500">
                            <p className="mb-2">Add-ons are one-time purchases and will remain active until cancelled.</p>
                            <p>For annual subscriptions, please contact support.</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Alert Dialog */}
            <AlertDialog
                isOpen={alert.isOpen}
                onClose={() => setAlert({ ...alert, isOpen: false })}
                title={alert.title}
                message={alert.message}
                type={alert.type}
            />

            {/* Confirm Dialog */}
            <ConfirmDialog
                isOpen={confirm.isOpen}
                onClose={() => setConfirm({ ...confirm, isOpen: false })}
                onConfirm={confirm.onConfirm || (() => {})}
                title={confirm.title}
                message={confirm.message}
                type={confirm.type}
            />
        </div>
    );
};

export default PurchaseAddon;
